import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined"
import { Box } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { customPalette } from "../../../../theme"
import { CreatedByDisplayer } from "../../../components/CreatedByDisplayer"
import { IconBox } from "../../../components/custom-icons/icon-box"
import { IconFileAnalytics } from "../../../components/custom-icons/icon-file-analytics"
import { IconInbox } from "../../../components/custom-icons/icon-inbox"
import { IconInfo } from "../../../components/custom-icons/icon-info"
import { IconUsers } from "../../../components/custom-icons/icon-users"
import { CustomTabPanel } from "../../../components/tabs/CustomTabPanel"
import { TabPanelProps } from "../../../components/tabs/TabPanel"
import { isProjectDetailTab, TabEnum, tabEnumFromStringNumber } from "../../../components/tabs/tabs"
import { pagesUrl } from "../../../core/appConstants"
import { AppSideBarContext } from "../../../core/context/AppSideBarContext/AppSideBarContext"
import { AppNavBarContext } from "../../../core/context/nav-bar/AppNavBarContext"
import { CoverImgProjectContext } from "../../../core/context/project/CoverImgProjectContext"
import { ProjectContext } from "../../../core/context/project/ProjectContext"
import { ProjectCountersContext } from "../../../core/context/project/ProjectCountersContext"
import { ProjectStatusEnum } from "../../../core/enum/projectStatusEnum"
import { resolveUrl } from "../../../core/services/http-service"
import { BeemShotTab } from "./tabs/BeemShotTab/BeemShotTab"
import { BSBimModelTab } from "./tabs/BimModelTab/BSBimModelTab"
import { BeemPilotTab } from "./tabs/BimPilotTab/BeemPilotTab"
import { ProjectGeneralTab } from "./tabs/ProjectGeneralTab.tsx/ProjectGeneralTab"
import { TeamTab } from "./tabs/TeamTab/TeamTab"
import { TeamContext } from "./tabs/TeamTab/context/TeamContext"
import { ReEstimatingDialogContextProvider } from "../components/ReEstimatingDialog/ReEstimatingDialogContext"

export function ProjectOverviewPage(): React.JSX.Element {
  return (
    <ReEstimatingDialogContextProvider>
      <ProjectOverviewPageCore />
    </ReEstimatingDialogContextProvider>
  )
}
export function ProjectOverviewPageCore(): React.JSX.Element {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const { setShowAppSide, setFullScreen } = useContext(AppSideBarContext)
  const {
    setTypeNavBar,
    setPreviousUrl,
    setStepHeader,
    setShowProjectStatus,
    setTitle,
    setImageFile,
    setBreadcrumbSteps,
    setIcon,
  } = useContext(AppNavBarContext)
  const { project } = useContext(ProjectContext)
  const {
    rseeDocumentNumber,
    isRseeDocumentNumberReady,
    variantNumber,
    isVariantNumberReady,
    bimModelNumber,
    isBimModelNumberReady,
    refreshAllCounts,
  } = useContext(ProjectCountersContext)
  const { file } = useContext(CoverImgProjectContext)
  const { currentTeam, isLoading } = useContext(TeamContext)

  const [currentTabId, setCurrentTabId] = React.useState<TabEnum>(TabEnum.PROJECT_GENERAL_INFORMATIONS)

  useEffect(() => {
    refreshAllCounts()
  }, [refreshAllCounts])

  useEffect(() => {
    const urlTabParam = searchParams.get("tab")
    const tabEnumParam = tabEnumFromStringNumber(urlTabParam)

    let tabEnum: TabEnum
    if (tabEnumParam && isProjectDetailTab(tabEnumParam)) {
      tabEnum = tabEnumParam
      setCurrentTabId(tabEnum)
    }
  }, [projectId, searchParams])

  useEffect(() => {
    setPreviousUrl(pagesUrl.PROJECTS_PAGE)
    setShowProjectStatus(true)
    setTitle(project?.name ?? "")
    setImageFile(file)
    setIcon(<IconInbox sx={{ color: customPalette.black, fontWeight: "2px" }} />)
    setBreadcrumbSteps([{ label: "Projets", url: resolveUrl(pagesUrl.PROJECTS_PAGE, [projectId]) }])
    setFullScreen(true)
    setShowAppSide(true)
    return () => {
      setShowProjectStatus(false)
      setFullScreen(false)
      setTitle("BEEM SHOT")
      setImageFile(undefined)
    }
  }, [
    file,
    navigate,
    currentTabId,
    setFullScreen,
    project?.name,
    projectId,
    setIcon,
    setImageFile,
    setBreadcrumbSteps,
    setPreviousUrl,
    setShowProjectStatus,
    setStepHeader,
    setTitle,
    setTypeNavBar,
    setShowAppSide,
  ])

  useEffect(() => {
    setShowAppSide(true)
  }, [setShowAppSide])

  const handleChange: (event: React.SyntheticEvent, newValue: TabEnum, sendInvitation?: boolean) => void = useCallback(
    (event, newValue, sendInvitation) => {
      setCurrentTabId(newValue)

      const params = new URLSearchParams(location.search)
      params.delete("tab")
      params.set("tab", newValue.toString())

      if (sendInvitation) {
        params.set("openInvitation", "true")
      } else {
        params.delete("openInvitation")
      }

      navigate(
        {
          pathname: location.pathname,
          search: params.toString(),
        },
        { replace: true }
      )
    },
    [location.pathname, location.search, navigate]
  )

  const projectOverviewTabs = useMemo(
    (): TabPanelProps[] => [
      {
        id: TabEnum.PROJECT_GENERAL_INFORMATIONS,
        Icon: IconInfo,
        label: "Général",
        chipLabel: undefined,
        isChipReady: true,
        content: <ProjectGeneralTab handleChange={handleChange} />,
        toolTipText: "Renseignez les informations clés de votre projet : localisation, date de dépôt PC, etc.",
      },
      {
        id: TabEnum.PROJECT_TEAM,
        Icon: IconUsers,
        label: "Équipe",
        chipLabel: currentTeam?.length ? currentTeam?.length.toString() : "", // This also avoid to display 0 while loading teams data
        isChipReady: !isLoading,
        content: <TeamTab />,
        toolTipText: "Invitez vos collaborateurs et constituez l’équipe idéale autour de votre projet.",
      },
      {
        id: TabEnum.PROJECT_BIM_MODEL,
        Icon: IconBox,
        label: "Maquettes Beem",
        chipLabel: bimModelNumber?.toString(),
        isChipReady: isBimModelNumberReady,
        content: <BSBimModelTab />,
        toolTipText: "Exploitez les quantitatifs issus de votre maquette IFC pour fiabiliser vos estimations carbone.",
      },
      {
        id: TabEnum.PROJECT_BEEM_SHOT,
        Icon: DonutSmallOutlinedIcon,
        label: "Shot",
        chipLabel: variantNumber?.toString(),
        isChipReady: isVariantNumberReady,
        content: <BeemShotTab />,
        disabled: false,
        disabledText: "Beem Shot n'est pas activé sur ce projet",
        toolTipText: "Réalisez des estimations carbone rapides et fiables, avec ou sans maquette numérique.",
      },
      {
        id: TabEnum.PROJECT_BEEM_PILOT,
        Icon: IconFileAnalytics,
        label: "Pilot",
        chipLabel: rseeDocumentNumber?.toString(),
        isChipReady: isRseeDocumentNumberReady,
        content: <BeemPilotTab />,
        disabled: false,
        disabledText: "Beem Pilot n'est pas activé sur ce projet",
        toolTipText: "Transformez vos RSEE en tableaux de bord carbone pour piloter efficacement votre projet.",
      },
    ],
    [
      handleChange,
      currentTeam.length,
      isLoading,
      bimModelNumber,
      isBimModelNumberReady,
      variantNumber,
      isVariantNumberReady,
      rseeDocumentNumber,
      isRseeDocumentNumberReady,
    ]
  )

  return (
    // The bottom padding is for the pages with a bottom button bar, so we can scroll low enough so it's not in front of some content
    <Box sx={{ pb: 10 }}>
      {project && project.status === ProjectStatusEnum.ARCHIVED && (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            color: customPalette.white,
            borderRadius: 4,
            backgroundColor: customPalette.dark1,
            my: 1,
          }}>
          Projet Archivé
        </Box>
      )}
      {project && project.status === ProjectStatusEnum.FINISHED && (
        <Box
          display="flex"
          justifyContent="center"
          sx={{
            color: customPalette.white,
            borderRadius: 4,
            backgroundColor: customPalette.dark1,
            my: 1,
          }}>
          Projet Terminé
        </Box>
      )}

      <CreatedByDisplayer
        createdByUser={project?.createdByUser}
        lastModifiedDate={project?.lastModifiedDate}
        department={project?.departmentId}
      />

      <CustomTabPanel panels={projectOverviewTabs} currentTabId={currentTabId} handleChangeTab={handleChange} />
    </Box>
  )
}
